/**
 * Compact "Evo Team" / "PSC Team" role chip used inside the notes
 * thread to disambiguate authors. Returns null for FP_USER so no
 * leaked FP note author renders as anything other than their name
 * (we never expect this in practice; the notes API forbids FP
 * users from writing notes at all).
 *
 * Sizing: deliberately small + quiet so it reads as supporting
 * metadata next to the author name, not as a status pill.
 */
import React from 'react'
import { Chip } from '@mui/material'
import { poolwerxColors } from '../../theme/colors'

export interface RoleBadgeUser {
  role?: string | null
}

export interface RoleBadgeProps {
  user: RoleBadgeUser
  /** Drop the role-specific colour for a muted grey treatment, so the chip
   *  reads as quiet metadata next to a name (used in the notes thread). */
  quiet?: boolean
}

const RoleBadge: React.FC<RoleBadgeProps> = ({ user, quiet = false }) => {
  const role = user?.role
  const isEvo = role === 'EVO_ADMIN' || role === 'EVO_TEAM'
  const isPsc = role === 'PSC_TEAM'
  if (!isEvo && !isPsc) return null
  const label = isEvo ? 'Evo Team' : 'PSC Team'
  // Evo = teal; PSC = soft secondary grey so the two read as distinct
  // tones without either dominating the author line. ``quiet`` mutes both
  // to grey so the chip never competes with the author name.
  const fg = quiet
    ? poolwerxColors.grey[600]
    : isEvo
      ? poolwerxColors.teal
      : poolwerxColors.grey[500]
  const borderColor = quiet
    ? poolwerxColors.grey[300]
    : isEvo
      ? poolwerxColors.teal
      : poolwerxColors.grey[300]
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        height: 16,
        fontWeight: 600,
        fontSize: '0.6rem',
        color: fg,
        borderColor,
        backgroundColor: '#FFFFFF',
        ml: 0.5,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  )
}

export default RoleBadge
